import React, { useState, useEffect, useRef } from 'react';
import { AvatarImage } from '@/components/ui/avatar';
import { supabaseClient } from '@/database/supabase-client';

interface CachedAvatarImageProps {
  src?: string | null;
  alt?: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

// **ÜBERARBEITETES CACHE-SYSTEM**
// 1. Einheitlicher Cache pro URL (nicht pro Größe)
// 2. Aggressive Browser-Cache-Nutzung
// 3. Service Worker Integration
const avatarCache = new Map<string, string>();
const loadingPromises = new Map<string, Promise<string>>();

// **CACHE-KONFIGURATION - Neue Version für hochauflösende Bilder**
const AVATAR_CACHE_KEY = 'ns_avatar_cache_v7'; // **NEU: v7 für hochauflösende Bilder (96px/90%)**
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 Tage

interface CacheEntry {
  url: string;
  timestamp: number;
  originalUrl: string; // Neue Eigenschaft
}

// **OPTIMIERTE CACHE-SPEICHERUNG**
const getCacheKey = (originalUrl: string): string => {
  // Einheitlicher Key ohne Größe
  return btoa(originalUrl).slice(0, 16);
};

const getFromLocalStorage = (originalUrl: string): string | null => {
  try {
    const cacheKey = getCacheKey(originalUrl);
    const cached = localStorage.getItem(`${AVATAR_CACHE_KEY}_${cacheKey}`);
    if (cached) {
      const entry: CacheEntry = JSON.parse(cached);
      const now = Date.now();
      if (now - entry.timestamp < CACHE_DURATION) {
        // **WICHTIG: Cache HIT protokollieren**
        return entry.url;
      } else {
        // Cache abgelaufen, entfernen
        localStorage.removeItem(`${AVATAR_CACHE_KEY}_${cacheKey}`);
      }
    }
  } catch (error) {
    console.warn('[CachedAvatarImage] localStorage read error:', error);
  }
  return null;
};

const saveToLocalStorage = (originalUrl: string, optimizedUrl: string): void => {
  try {
    const cacheKey = getCacheKey(originalUrl);
    const entry: CacheEntry = {
      url: optimizedUrl,
      timestamp: Date.now(),
      originalUrl: originalUrl.slice(-30) // Nur letzte 30 Zeichen speichern
    };
    localStorage.setItem(`${AVATAR_CACHE_KEY}_${cacheKey}`, JSON.stringify(entry));
  } catch (error) {
    console.warn('[CachedAvatarImage] localStorage write error:', error);
  }
};

// **HAUPTFUNKTION: Optimiertes Avatar-Loading mit korrekter Supabase Transform API**
const getCachedAvatarUrl = async (originalUrl: string, size: 'small' | 'medium' | 'large' = 'small'): Promise<string> => {
  // **KRITISCH: Einheitlicher Cache-Key ohne Größe**
  const memoryCacheKey = originalUrl;
  
  // 1. Memory Cache Check
  if (avatarCache.has(memoryCacheKey)) {
    return avatarCache.get(memoryCacheKey)!;
  }

  // 2. Check if already loading
  if (loadingPromises.has(memoryCacheKey)) {
    return loadingPromises.get(memoryCacheKey)!;
  }

  // 3. localStorage Cache Check
  const cachedUrl = getFromLocalStorage(originalUrl);
  if (cachedUrl) {
    avatarCache.set(memoryCacheKey, cachedUrl);
    return cachedUrl;
  }

  // 4. **NEUE LÖSUNG: Korrekte Supabase SDK Transform verwenden**
  const loadPromise = new Promise<string>((resolve) => {
    // Parse originalUrl um Bucket und Pfad zu extrahieren
    try {
      if (originalUrl.includes('supabase.co/storage/v1/object/public/')) {
        // Statischen Import verwenden statt dynamischen Import
        const urlParts = originalUrl.split('/storage/v1/object/public/');
        if (urlParts.length === 2) {
          const [bucket, ...pathParts] = urlParts[1].split('/');
          const filePath = pathParts.join('/');
          
          
          // **DRASTISCH ERHÖHTE Supabase SDK Transform-API für scharfe Bilder**
          const { data } = supabaseClient.client.storage
            .from(bucket)
            .getPublicUrl(filePath, {
              transform: {
                width: size === 'small' ? 96 : size === 'medium' ? 128 : 256, // **VIEL GRÖSSER: 96px für small!**
                height: size === 'small' ? 96 : size === 'medium' ? 128 : 256,
                quality: 90 // **BESTE QUALITÄT: 90% für scharfe Bilder**
              }
            });
          
          const finalUrl = data.publicUrl;
          
          // Cache speichern
          avatarCache.set(memoryCacheKey, finalUrl);
          saveToLocalStorage(originalUrl, finalUrl);
          
          resolve(finalUrl);
        } else {
          throw new Error('Cannot parse Supabase URL');
        }
      } else {
        // Nicht-Supabase URLs unverändert zurückgeben
        avatarCache.set(memoryCacheKey, originalUrl);
        resolve(originalUrl);
      }
    } catch (error) {
      console.warn('[AvatarCache] Transform failed, using fallback:', error);
      // Fallback zur Original-URL
      avatarCache.set(memoryCacheKey, originalUrl);
      resolve(originalUrl);
    }
  });

  loadingPromises.set(memoryCacheKey, loadPromise);
  
  const result = await loadPromise;
  loadingPromises.delete(memoryCacheKey);
  
  return result;
};

export const CachedAvatarImage: React.FC<CachedAvatarImageProps> = ({ 
  src, 
  alt = 'Avatar', 
  className,
  size = 'small'
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!src) {
      setImageSrc(null);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    // Check if signed URL or secure avatar edge function URL
    if (
      src.includes('?token=') ||
      src.includes('/functions/v1/secure-avatar')
    ) {
      // Use secure/service URLs directly without caching or transformation
      setImageSrc(src);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    getCachedAvatarUrl(src, size)
      .then((url) => {
        if (isMountedRef.current) {
          setImageSrc(url);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error('[CachedAvatarImage] Error loading avatar:', error);
        if (isMountedRef.current) {
          setHasError(true);
          setIsLoading(false);
          // Fallback to original URL
          setImageSrc(src);
        }
      });
  }, [src, size]);

  if (!imageSrc) {
    return null;
  }

  return (
    <AvatarImage 
      src={imageSrc} 
      alt={alt} 
      className={className}
      onError={(e) => {
        console.error('[CachedAvatarImage] Image load error:', e);
        console.error('[CachedAvatarImage] Failed URL:', imageSrc);
        setHasError(true);
      }}
      onLoad={() => {
      }}
    />
  );
};

// **NEU: Spezifische URL-basierte Cache-Invalidierung**
export const invalidateAvatarForUrl = (avatarUrl: string): void => {
  if (!avatarUrl) return;
  
  
  // **1. Memory Cache löschen**
  if (avatarCache.has(avatarUrl)) {
    avatarCache.delete(avatarUrl);
  }
  
  // **2. localStorage löschen**
  try {
    const cacheKey = getCacheKey(avatarUrl);
    const key = `${AVATAR_CACHE_KEY}_${cacheKey}`;
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn('[CachedAvatarImage] Error invalidating avatar cache for URL:', error);
  }
  
  // **3. Service Worker Cache invalidieren**
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'INVALIDATE_AVATAR_URL',
        url: avatarUrl
      });
    }
  } catch (error) {
    console.warn('[AvatarCache] Service Worker invalidation failed:', error);
  }
};

// **NEU: Präventive Cache-Preloading**
export const preloadAvatarUrl = async (avatarUrl: string): Promise<void> => {
  if (!avatarUrl || avatarCache.has(avatarUrl)) return;
  
  try {
    await getCachedAvatarUrl(avatarUrl, 'small');
  } catch (error) {
    console.warn('[AvatarCache] Preload failed:', error);
  }
};

// **VERBESSERTE HTTP-Cache-Implementierung**
const createImageWithCacheHeaders = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // **WICHTIG: crossOrigin für bessere Cache-Kontrolle**
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // **Bild erfolgreich geladen**
      resolve(url);
    };
    
    img.onerror = () => {
      console.warn('[AvatarCache] ❌ Image load failed:', url.slice(-20));
      reject(new Error('Image load failed'));
    };
    
    // **Cache-Friendly Approach: Stabile URLs verwenden**
    img.src = url;
  });
};

// **VERBESSERTE Cache-Bereinigung mit Service Worker**
export const clearAvatarCache = (): void => {
  
  // 1. Memory Cache
  avatarCache.clear();
  
  // 2. localStorage bereinigen
  try {
    const keys = Object.keys(localStorage);
    const removedKeys: string[] = [];
    keys.forEach(key => {
      if (key.startsWith(AVATAR_CACHE_KEY)) {
        localStorage.removeItem(key);
        removedKeys.push(key);
      }
    });
  } catch (error) {
    console.warn('[CachedAvatarImage] Error clearing localStorage cache:', error);
  }
  
  // 3. Service Worker Cache bereinigen
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_AVATAR_CACHE'
      });
    }
  } catch (error) {
    console.warn('[AvatarCache] Service Worker cache clear failed:', error);
  }
};

// Debug-Funktion für Cache-Status
export const getAvatarCacheStatus = (): { memoryCache: number; localStorageKeys: string[] } => {
  const localStorageKeys = [];
  try {
    const keys = Object.keys(localStorage);
    localStorageKeys.push(...keys.filter(key => key.startsWith(AVATAR_CACHE_KEY)));
  } catch (error) {
    console.warn('[CachedAvatarImage] Error reading localStorage keys:', error);
  }
  
  return {
    memoryCache: avatarCache.size,
    localStorageKeys
  };
};

// **NEU: Alle alten Cache-Versionen löschen**
export const clearAllAvatarCaches = (): void => {
  
  // Memory Cache leeren
  avatarCache.clear();
  
  // localStorage: alle alten Versionen löschen
  const oldVersions = ['ns_avatar_cache_v1', 'ns_avatar_cache_v2', 'ns_avatar_cache_v3'];
  try {
    for (const prefix of oldVersions) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch (error) {
    console.warn('[AvatarCache] Error clearing localStorage:', error);
  }
  
  // Service Worker Cache leeren
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_AVATAR_CACHE'
    });
  }
  
}; 